import { useState, useCallback, useRef, useEffect } from "react";
import { ArrowLeft, MapPin, Crosshair, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Map } from "@vis.gl/react-google-maps";
import PlacesAutocomplete from "@/components/PlacesAutocomplete";

const BENGALURU_CENTER = { lat: 12.9716, lng: 77.5946 };

// Pick the most precise reverse-geocode result: prefer street-level address
// types over establishment/POI names. Google's default first-result returns
// the nearest named feature, which makes "I dragged the pin off the building
// onto the road" still display the building name and feel broken. Preference
// order matches how operators actually read pickup directions.
const GEOCODE_TYPE_PREFERENCE = [
  "street_address",
  "premise",
  "subpremise",
  "route",
  "intersection",
  "neighborhood",
  "establishment",
];

function pickBestGeocodeResult(results) {
  if (!Array.isArray(results) || results.length === 0) return null;
  for (const type of GEOCODE_TYPE_PREFERENCE) {
    const match = results.find((r) => Array.isArray(r.types) && r.types.includes(type));
    if (match) return match;
  }
  return results[0];
}

const LocationPicker = ({ isOpen, onClose, onConfirm, label = "Pick-up Location" }) => {
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState(null);
  const [mapCenter, setMapCenter] = useState(BENGALURU_CENTER);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const geocoderRef = useRef(null);

  // Initialize geocoder
  useEffect(() => {
    if (window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isOpen]);

  const handlePlaceSelect = useCallback((displayText, placeCoords) => {
    setAddress(displayText);
    if (placeCoords) {
      setCoords(placeCoords);
      setMapCenter(placeCoords);
    }
  }, []);

  // Shared reverse-geocode used by drag, click-on-empty-tile, the POI failure
  // fallback, and "use current location". Prefers street-level results via
  // pickBestGeocodeResult(); on failure falls back to a "Pin: lat, lng" label
  // so the picker is always usable (e.g. if the Geocoding API ever returns
  // REQUEST_DENIED again).
  const reverseGeocodeFallback = useCallback((newCoords) => {
    if (!geocoderRef.current) {
      setAddress(`Pin: ${newCoords.lat.toFixed(6)}, ${newCoords.lng.toFixed(6)}`);
      return;
    }
    geocoderRef.current.geocode({ location: newCoords }, (results, status) => {
      if (status === "OK") {
        const pick = pickBestGeocodeResult(results);
        if (pick) setAddress(pick.formatted_address);
      } else {
        setAddress(`Pin: ${newCoords.lat.toFixed(6)}, ${newCoords.lng.toFixed(6)}`);
      }
    });
  }, []);

  // Reverse-geocode whenever the user finishes a map drag. We DON'T re-set
  // `mapCenter` here because that would change the <Map>'s `key` prop and
  // force a full remount — wiping the user's drag and snapping the map back
  // to its remounted defaultCenter. Just update coords + address.
  const handleMapDragEnd = useCallback((e) => {
    const center = e.map.getCenter();
    if (!center) return;
    const newCoords = { lat: center.lat(), lng: center.lng() };
    setCoords(newCoords);
    reverseGeocodeFallback(newCoords);
  }, [reverseGeocodeFallback]);

  // Click anywhere on the map → re-centre the picker pin there and pick that
  // spot as the pickup. Two paths:
  //   1. POI click (placeId present on event.detail) — the user tapped a
  //      restaurant / hotel / office / school icon. Fetch the named place via
  //      PlacesService so the address is "Toit, 100 Feet Rd, ..." instead of
  //      a generic street-level result.
  //   2. Empty-tile click (no placeId) — reverse-geocode the latLng.
  // We call e.stop() in both paths so Google's default click-on-POI infowindow
  // ("View on Google Maps") doesn't pop up over our own pin and confuse users.
  const handleMapClick = useCallback((e) => {
    const ll = e?.detail?.latLng;
    if (!ll) return;
    // Suppress Google's default POI infowindow — we render our own "Selected
    // location" footer below the map for the same purpose.
    if (typeof e.stop === "function") e.stop();
    const newCoords = { lat: ll.lat, lng: ll.lng };
    setCoords(newCoords);
    // Smooth recentre keeps the map's other state (zoom, tilt) intact —
    // unlike updating `mapCenter` which would force a full Map remount and
    // visually snap the map.
    if (e.map?.panTo) {
      e.map.panTo(ll);
    }
    const placeId = e?.detail?.placeId;
    // POI click → resolve the named place via PlacesService.
    if (placeId && window.google?.maps?.places?.PlacesService && e.map) {
      const svc = new window.google.maps.places.PlacesService(e.map);
      svc.getDetails(
        { placeId, fields: ["name", "formatted_address", "geometry"] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const name = place.name || "";
            const addr = place.formatted_address || "";
            setAddress(name && name !== addr ? `${name}, ${addr}` : (addr || name));
            // Snap pin to the place's actual geometry rather than the raw
            // click coords if available (clicks land at the icon, not the
            // building footprint).
            if (place.geometry?.location && e.map?.panTo) {
              e.map.panTo(place.geometry.location);
              setCoords({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
            }
          } else {
            // PlacesService failed → fall through to reverse-geocode the
            // raw click coords so we always have an address to show.
            reverseGeocodeFallback(newCoords);
          }
        }
      );
      return;
    }
    // Empty-tile click (no POI) → reverse-geocode.
    reverseGeocodeFallback(newCoords);
  }, [reverseGeocodeFallback]);

  // Detect current location
  const handleDetectLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(newCoords);
        setMapCenter(newCoords);
        reverseGeocodeFallback(newCoords);
        setDetectingLocation(false);
      },
      () => { setDetectingLocation(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [reverseGeocodeFallback]);

  const handleConfirm = () => {
    onConfirm(address, coords);
    // Reset for next use
    setAddress("");
    setCoords(null);
    setMapCenter(BENGALURU_CENTER);
    onClose();
  };

  const handleBack = () => {
    setAddress("");
    setCoords(null);
    setMapCenter(BENGALURU_CENTER);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="fixed inset-0 z-[60] bg-[#050505] flex flex-col"
          data-testid="location-picker"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
            <button
              type="button"
              onClick={handleBack}
              className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#a1a1aa] hover:text-white transition-colors"
              data-testid="location-picker-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="font-outfit text-lg font-bold text-white">{label}</h2>
          </div>

          {/* Search bar */}
          <div className="px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3 bg-[#111111] border border-white/[0.08] rounded-xl px-4 py-3">
              <MapPin className="w-5 h-5 text-sarathi-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <PlacesAutocomplete
                  value={address}
                  onChange={handlePlaceSelect}
                  placeholder="Search for a location"
                  testId="location-picker-search"
                />
              </div>
            </div>

            {/* Use current location */}
            <button
              type="button"
              onClick={handleDetectLocation}
              disabled={detectingLocation}
              className="flex items-center gap-3 w-full mt-3 px-4 py-3 text-left hover:bg-white/[0.03] rounded-xl transition-colors"
              data-testid="detect-location-btn"
            >
              {detectingLocation ? (
                <Loader2 className="w-5 h-5 text-sarathi-primary animate-spin shrink-0" />
              ) : (
                <Crosshair className="w-5 h-5 text-sarathi-primary shrink-0" />
              )}
              <span className="text-[#a1a1aa] text-sm font-medium">
                {detectingLocation ? "Detecting location..." : "Use your current location"}
              </span>
            </button>
          </div>

          {/* Map — shown after address is selected */}
          <div className="flex-1 relative">
            {coords ? (
              <>
                <Map
                  mapId="sarathix-location-picker"
                  defaultCenter={mapCenter}
                  defaultZoom={16}
                  gestureHandling="greedy"
                  zoomControl={true}
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                  onClick={handleMapClick}
                  onDragend={handleMapDragEnd}
                  onZoomChanged={handleMapDragEnd}
                  className="w-full h-full"
                  style={{ width: "100%", height: "100%" }}
                  key={`${mapCenter.lat}-${mapCenter.lng}`}
                />

                {/* Center pin overlay (stays centered when map drags) */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-10">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-sarathi-primary rounded-full flex items-center justify-center shadow-lg">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="w-2 h-2 bg-sarathi-primary rounded-full mt-0.5 opacity-50" />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-[#3f3f46]">
                <div className="text-center px-8">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Search for an address or use your current location</p>
                </div>
              </div>
            )}
          </div>

          {/* Bottom bar — address + confirm */}
          {coords && address && (
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="border-t border-white/[0.06] bg-[#111111] px-5 py-4"
            >
              <p className="text-[#a1a1aa] text-xs mb-1">Selected location</p>
              <p className="text-white text-sm font-medium mb-4 line-clamp-2" data-testid="selected-address">{address}</p>
              <p className="text-[#52525b] text-[11px] text-center mb-3">Tap or drag the map to set your exact pickup spot</p>
              <button
                type="button"
                onClick={handleConfirm}
                className="w-full py-4 rounded-2xl bg-sarathi-primary hover:bg-orange-600 text-white font-bold text-[15px] transition-all shadow-[0_4px_24px_rgba(249,115,22,0.25)]"
                data-testid="confirm-location-btn"
              >
                Confirm Location
              </button>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LocationPicker;
